// Employer vs. employee framing of the atlas: section order, copy, checklists and employee baseline.
import type { BaselineRule } from './data'

export type View = 'er' | 'ee'

export type SectionId =
  | 'checklist'
  | 'baseline'
  | 'explorer'
  | 'exceptions'
  | 'health'
  | 'unemployment'
  | 'age'
  | 'family'
  | 'retraining'
  | 'table'
  | 'discrepancies'
  | 'sources'

export const SECTION_ORDER: Record<View, { id: SectionId; label: string }[]> = {
  er: [
    { id: 'checklist', label: 'Checklist' },
    { id: 'baseline', label: 'Common to all states' },
    { id: 'explorer', label: 'State explorer' },
    { id: 'exceptions', label: 'Exceptions' },
    { id: 'health', label: 'Health coverage' },
    { id: 'unemployment', label: 'Severance & UI' },
    { id: 'age', label: 'Age' },
    { id: 'family', label: 'Spouse & family' },
    { id: 'retraining', label: 'Retraining' },
    { id: 'table', label: 'All 50 states' },
    { id: 'discrepancies', label: 'Discrepancies' },
    { id: 'sources', label: 'Sources' },
  ],
  ee: [
    { id: 'checklist', label: 'First 30 days' },
    { id: 'baseline', label: 'Your rights' },
    { id: 'health', label: 'Health coverage' },
    { id: 'unemployment', label: 'Unemployment' },
    { id: 'explorer', label: 'Your state' },
    { id: 'age', label: 'Age' },
    { id: 'family', label: 'Spouse & family' },
    { id: 'retraining', label: 'Retraining' },
    { id: 'exceptions', label: 'State exceptions' },
    { id: 'table', label: 'All 50 states' },
    { id: 'discrepancies', label: 'Discrepancies' },
    { id: 'sources', label: 'Sources' },
  ],
}

type Copy = { title: string; body?: string }

export const SECTION_COPY: Record<SectionId, Record<View, Copy>> = {
  checklist: {
    er: {
      title: 'Layoff checklist',
      body: 'The steps in the order they usually come up. Each links to the rules further down the page. Ticks are saved in this browser only.',
    },
    ee: {
      title: 'Your first 30 days: checklist',
      body: 'What to do, roughly in order, after you’re told your job is ending. Ticks are saved in this browser only.',
    },
  },
  baseline: {
    er: {
      title: 'Common to all 50 states',
      body: 'These rules apply no matter where the employee works. Every state exception sits on top of this layer.',
    },
    ee: {
      title: 'Your rights everywhere in the US',
      body: 'These apply in every state. Your state can add more; check it in the explorer.',
    },
  },
  explorer: {
    er: {
      title: 'State explorer',
      body: 'Pick a lens to recolor the map, then select a state for its full profile. Darker means a stronger obligation on the employer. A yellow dot means the sources disagree about that state.',
    },
    ee: {
      title: 'What your state adds',
      body: 'Select the state where you worked. Darker means stronger protections for you. A yellow dot means the sources disagree about that state.',
    },
  },
  exceptions: {
    er: {
      title: 'Exceptions by theme',
      body: 'States grouped by the ways they go beyond the federal baseline. Select a state code to open its profile.',
    },
    ee: {
      title: 'States with extra protections',
      body: 'States grouped by the protections they add. Select a state code to open its profile.',
    },
  },
  health: {
    er: {
      title: 'Health coverage after the job ends',
      body: 'COBRA, the state continuation laws that cover smaller employers, Medicare, and individual insurance from the ACA marketplace. Several of these deadlines start on the last day of work, so the order matters.',
    },
    ee: {
      title: 'Keeping health coverage',
      body: 'Your options are COBRA, state continuation, a spouse’s plan, the ACA marketplace, Medicaid or Medicare. Several deadlines start on your last day, so the order matters.',
    },
  },
  unemployment: {
    er: {
      title: 'How severance affects unemployment benefits',
      body: 'State unemployment agencies treat severance very differently. How you pay it (a lump sum or installments, and whether it’s tied to a signed release) can decide whether former employees get benefits right away.',
    },
    ee: {
      title: 'Can you collect unemployment and severance?',
      body: 'In most states, yes, eventually. But severance can delay or reduce benefits depending on your state and how it’s paid. File for unemployment in your first week either way and report the severance; the agency decides.',
    },
  },
  age: {
    er: {
      title: 'Does the employee’s age change anything?',
      body: 'Yes, at a handful of specific ages. The core severance, final-pay, WARN and PTO rules are the same at every age.',
    },
    ee: {
      title: 'Does your age change anything?',
      body: 'Yes, at a handful of specific ages. Your final pay, vacation payout and notice rights are the same at every age.',
    },
  },
  family: {
    er: {
      title: 'Spouses and families',
      body: 'Severance, final pay and WARN rules never depend on the employee’s spouse. Health coverage does, and it has the most spouse-specific rules.',
    },
    ee: {
      title: 'Your spouse and family',
      body: 'Your severance and final pay don’t depend on your spouse. Your family’s health coverage does, and each family member has their own rights.',
    },
  },
  retraining: {
    er: {
      title: 'Retraining programs',
      body: 'Federal programs run through each state’s American Job Centers. A WARN notice is what triggers the state’s Rapid Response team, so notice laws and retraining are linked.',
    },
    ee: {
      title: 'Free retraining and job help',
      body: 'Federal and state programs can pay for training, and in some states you keep collecting unemployment while you train. Start at an American Job Center.',
    },
  },
  table: {
    er: {
      title: 'All 50 states side by side',
      body: 'Search or filter, sort by clicking a column heading, and click a row to open that state in the explorer.',
    },
    ee: {
      title: 'All 50 states side by side',
      body: 'Search or filter, sort by clicking a column heading, and click a row to open that state in the explorer.',
    },
  },
  discrepancies: {
    er: {
      title: 'Where the sources disagree',
      body: 'Each item compares what the source articles say against the statute or a recent primary or law-firm source. High means the error could change a compliance decision. Medium means a wrong number or an outdated law. Low means imprecise or incomplete wording.',
    },
    ee: {
      title: 'Where popular guides get it wrong',
      body: 'Popular online guides disagree with each other and sometimes with the law. Each item shows what they say next to the verified rule. High means the error could cost you money or a deadline.',
    },
  },
  sources: { er: { title: 'Sources' }, ee: { title: 'Sources' } },
}

export const HERO_COPY: Record<View, string> = {
  er: 'What every US employer must do when someone leaves, and the states that add more: layoff notice, final pay, severance, releases, health coverage and state forms. Everything shown is the verified rule; where a source article said something different, it’s recorded in the discrepancy section and doesn’t change the data.',
  ee: 'What you’re owed when you lose your job, what to check before you sign a severance agreement, and the deadlines for health coverage, unemployment and retirement money that start on your last day. Everything shown is the verified rule; where a source article said something different, it’s recorded in the discrepancy section.',
}

/** Checklist items: [title, detail, section to link to]. */
export const CHECKLIST: Record<View, [string, string, SectionId][]> = {
  er: [
    [
      'Count heads against every notice law',
      'Federal WARN (100+ employees, 60 days) and your state’s law. Layoffs over a rolling 90-day window can add up. See the "Mini-WARN" lens.',
      'explorer',
    ],
    [
      'Check for mandatory severance',
      'New Jersey (1 week per year, 100+ employees nationwide, 50+ in NJ), Maine (closings of 100+ employee sites) and Hawaii (4-week top-up after closings).',
      'exceptions',
    ],
    [
      'Review who was selected',
      'Check the layoff list for disparate impact by age (40+), sex, race and other protected groups before notices go out.',
      'age',
    ],
    [
      'Draft the release for each state',
      'OWBPA for 40+ (21/45 + 7 days, group disclosures), and CA, IL, MN, NY and NJ add their own rules. Keep confidentiality and non-disparagement narrow for non-supervisors (McLaren Macomb).',
      'baseline',
    ],
    [
      'Decide how to pay severance',
      'Lump sum or installments, and whether it depends on a release, change how state unemployment agencies treat it. Installments must fit §409A.',
      'unemployment',
    ],
    [
      'Pay final wages and PTO on time',
      'Same day in CA, CO, MA, MO, MT and NV; within 24 hours in UT; next business day in CT and OR. PTO payout rules differ by state.',
      'explorer',
    ],
    [
      'Hand over state forms',
      'UI notices in 22 states (e.g., CA DE 2320, NJ BC-10, NY IA 12.3, TN LB-0489 within 24 hours) and NY’s 5-day written termination notice.',
      'explorer',
    ],
    [
      'Start COBRA and state continuation',
      'Notify the administrator within 30 days. Check state continuation for insured plans and extra employer-paid coverage in CT and MA closings.',
      'health',
    ],
    [
      'Check 401(k) partial plan termination',
      'If more than 20% of plan participants are cut in a plan year, affected participants may need to be fully vested.',
      'baseline',
    ],
    [
      'Contact the state Rapid Response team',
      'Mandatory where a WARN notice is filed, and useful anyway. They brief employees on unemployment, retraining and health options.',
      'retraining',
    ],
  ],
  ee: [
    [
      'Don’t sign on day one',
      'Note your deadline to decide (21 days if 40+, 45 in a group layoff; 5 business days in CA) and your 7-day right to cancel if 40+.',
      'baseline',
    ],
    [
      'Confirm your last paycheck and unused vacation',
      'Check your state’s deadline in the explorer. These are owed whether or not you sign anything.',
      'explorer',
    ],
    [
      'File for unemployment in week one',
      'Report any severance. Whether it delays or reduces benefits depends on your state and how it’s paid.',
      'unemployment',
    ],
    [
      'Write down the date your health coverage ends',
      'Then choose: a spouse’s plan (sign up within 30 days), the marketplace (within 60 days) or COBRA (60 days to elect, retroactive).',
      'health',
    ],
    [
      'If you’re 65+: sign up for Medicare Part B',
      'You have 8 months from the job ending, not from COBRA ending. Missing it means a lifetime penalty.',
      'health',
    ],
    [
      'Ask for what you’re entitled to see',
      'The severance plan document, the group-layoff age list if you’re 40+, and your COBRA election notice.',
      'baseline',
    ],
    [
      'Negotiate',
      'More weeks, paid COBRA, pro-rated bonus, longer option-exercise window, a neutral reference, and tax-friendly timing.',
      'baseline',
    ],
    [
      'Protect your retirement money',
      'Don’t cash out the 401(k) by reflex. Check the rule of 55, a 401(k) loan’s repayment deadline, and whether a large layoff triggered full vesting.',
      'baseline',
    ],
    [
      'Check your spouse’s and family’s coverage',
      'Spouse and children can elect COBRA separately, and some states give spouses more time (IL 55+, GA 60+).',
      'family',
    ],
    [
      'Visit an American Job Center',
      'WIOA dislocated-worker training is free if you qualify, and in WA, OR, ME and CA you can collect unemployment while you train.',
      'retraining',
    ],
  ],
}

export const BASE_EMPLOYEE: BaselineRule[] = [
  {
    h: 'Severance isn’t guaranteed, but it is negotiable',
    law: 'FLSA · contract law',
    pts: [
      'No federal law requires severance. Only New Jersey (mass layoffs), Maine (closings) and Hawaii (closings, a 4-week top-up) require payments.',
      'If severance was promised in an offer letter, contract, handbook or written plan, it’s enforceable. Ask HR for the severance plan document or summary plan description.',
      'Common things to ask for: more weeks of pay, paid COBRA premiums, pro-rated bonus, extra time to exercise stock options, a neutral reference, outplacement, and payment timing (e.g., split across January 1 for taxes).',
    ],
  },
  {
    h: 'Your final paycheck is owed no matter what',
    law: 'State wage-payment laws',
    pts: [
      'Earned wages can’t be withheld until you sign a release, and the deadline depends on your state (same day in California, Colorado and Massachusetts; the next payday in many others). Use the explorer.',
      'In about ten states, unused vacation must be paid out too.',
      'If it’s late, you can file a wage claim with your state labor department. Penalties can be large: up to 30 days’ pay in California, triple damages in Massachusetts.',
    ],
  },
  {
    h: 'If you’re 40 or older',
    law: 'ADEA · OWBPA',
    pts: [
      'You get at least 21 days to decide (45 if you’re part of a group layoff) and 7 days to cancel after signing. The agreement must tell you in writing to consult a lawyer.',
      'In a group layoff, the employer must give you a list of the job titles and ages of who was and wasn’t selected. Check it for a pattern of older workers being cut.',
      'You don’t have to use the full 21 days, but you can’t be pressured to sign sooner.',
    ],
  },
  {
    h: 'What a release can’t take away',
    law: 'EEOC · SEC · NLRA · ERISA',
    pts: [
      'Your right to file a charge with the EEOC or a state agency, or to report to the SEC and other regulators.',
      'Unemployment benefits, workers’ comp, and vested retirement benefits (your 401(k) balance and vested pension).',
      'If you’re not a supervisor, overly broad confidentiality or non-disparagement clauses may be unlawful under the NLRA.',
      'State add-ons: 5 business days in CA, 21/7 days in IL and NY for confidential discrimination settlements, and a 15-day right to cancel for Minnesota claims.',
    ],
  },
  {
    h: 'Mass-layoff notice',
    law: 'WARN + state laws',
    pts: [
      'If your employer has 100+ employees and 50+ people at your site are laid off, you’re generally owed 60 days’ notice or 60 days’ pay and benefits instead.',
      'Many states require more (90 days in NY, NJ, ME and NE). A short-notice payment is often what’s labeled "WARN pay" in your paperwork.',
    ],
  },
  {
    h: 'Taxes on your severance',
    law: 'IRS supplemental wages',
    pts: [
      'Severance is taxed as wages. Employers often withhold a flat 22% federally, which may be too little or too much for your bracket.',
      'A large lump sum late in the year can push you into a higher bracket. Asking for part of it in January is common and often granted.',
      'Severance counts toward income for marketplace subsidies in the year it’s paid.',
    ],
  },
  {
    h: 'Your 401(k), pension and equity',
    law: 'ERISA · IRC',
    pts: [
      'Your vested 401(k) balance is yours. Rolling it to an IRA or your next plan avoids tax. Cashing out usually brings tax plus a 10% penalty before 59½.',
      'Leaving in or after the year you turn 55 lets you withdraw from that employer’s 401(k) without the 10% penalty (the rule of 55).',
      'If more than 20% of plan participants are laid off, it can be a "partial plan termination," and affected workers become fully vested in employer contributions.',
      'An unpaid 401(k) loan is generally treated as a withdrawal unless repaid or rolled over by your tax-filing deadline.',
      'Stock options often must be exercised within 90 days of leaving. Check your grant agreement.',
    ],
  },
  {
    h: 'Why you were let go',
    law: 'At-will · service letters',
    pts: [
      'In 49 states employers don’t have to give a reason, but they can’t fire for an illegal one (discrimination, retaliation, whistleblowing, taking protected leave).',
      'Montana requires good cause after probation.',
      'In MO, KS, ME, MN, MT and IN you can request a written letter about your employment or the reason for termination.',
    ],
  },
]
